import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useParams } from "react-router-dom";

import SEO from "components/SEO";
import { trackAnalyticsEvent } from "api/analyticsApi";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ;

function useQueryParams() {
  const location = useLocation();
  return useMemo(() => new URLSearchParams(location.search), [location.search]);
}

function toBool(value, defaultValue = false) {
  if (value === null || value === undefined) return defaultValue;
  return String(value).toLowerCase() === "true";
}

function normalizeLayout(layout) {
  const value = String(layout || "").trim();

  if (!value) return "masonry";
  if (value === "masonry-animated") return "masonry";
  if (value === "masonry-fixed") return "masonry";
  if (value === "video") return "video-wall";

  return value;
}

function resolveFileUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("data:")) return url;
  if (url.startsWith("/")) return `${API_BASE_URL}${url}`;
  return `${API_BASE_URL}/${url}`;
}

function getInitials(name) {
  const safeName = String(name || "A").trim();
  return safeName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((x) => x[0]?.toUpperCase())
    .join("") || "A";
}

function getStars(value) {
  const count = Math.max(1, Math.min(5, Number(value || 5)));
  return "★".repeat(count) + "☆".repeat(5 - count);
}

export default function WallEmbed() {
  const { spaceSlug } = useParams();
  const query = useQueryParams();

  const layout = normalizeLayout(query.get("layout") || "masonry");
  const dark = toBool(query.get("dark"), false);
  const hideDate = toBool(query.get("hideDate"), false);
  const hideSource = toBool(query.get("hideSource"), false);
  const showCaptions = query.get("showCaptions") !== "false";
  const maxItems = Number(query.get("maxItems") || 50);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [space, setSpace] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;

    async function loadWall() {
      setLoading(true);
      setError("");

      try {
        const url = `${API_BASE_URL}/api/walloflove/embed/${encodeURIComponent(
          spaceSlug
        )}`;

        console.log("[WallEmbed] Calling API:", url);

        const res = await axios.get(url, {
          timeout: 20000,
          headers: {
            Accept: "application/json",
          },
        });

        if (cancelled) return;

        const data = res.data || {};
        const texts = Array.isArray(data.texts) ? data.texts : [];
        const videos = Array.isArray(data.videos) ? data.videos : [];

        const merged = [
          ...texts.map((item) => ({
            ...item,
            type: "text",
          })),
          ...videos.map((item) => ({
            ...item,
            type: "video",
          })),
        ];

        const filtered = merged
          .filter(Boolean)
          .sort((a, b) => {
            const da = new Date(a.createdAt || 0).getTime();
            const db = new Date(b.createdAt || 0).getTime();
            return db - da;
          })
          .slice(0, Number.isFinite(maxItems) ? maxItems : 50);

        setSpace(data.space || null);
        setItems(filtered);
        setLoading(false);

        trackAnalyticsEvent({
          eventType: "wall_embed_loaded",
          spaceId: data.space?._id,
          spaceSlug,
          source: "wall_embed",
          metadata: { layout, items: filtered.length },
        });

        console.log("[WallEmbed] Loaded items:", filtered.length);
      } catch (err) {
        if (cancelled) return;

        console.error("[WallEmbed] API failed:", err);

        setError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            err.message ||
            "Failed to load Wall of Love"
        );
        setLoading(false);
      }
    }

    loadWall();

    return () => {
      cancelled = true;
    };
  }, [spaceSlug, maxItems]);

  useEffect(() => {
    function sendHeight() {
      const height = Math.max(
        document.body.scrollHeight || 0,
        document.documentElement.scrollHeight || 0,
        300
      );

      window.parent.postMessage(
        {
          type: "testimonial-embed:resize",
          height,
          spaceSlug,
        },
        "*"
      );
    }

    sendHeight();

    let observer = null;

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(sendHeight);
      observer.observe(document.body);
    }

    const id = window.setInterval(sendHeight, 1000);

    return () => {
      if (observer) observer.disconnect();
      window.clearInterval(id);
    };
  }, [spaceSlug, loading, error, items.length, layout]);

  const pageStyle = {
    minHeight: "100vh",
    boxSizing: "border-box",
    padding: 20,
    background: dark ? "#020617" : "#ffffff",
    color: dark ? "#f8fafc" : "#111827",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
  };

  if (loading) {
    return (
      <>
      <SEO
  title="Wall of Love Embed - TestimonialsHub"
  description="Display beautiful customer testimonials and video reviews on your website using the TestimonialsHub Wall of Love embed widget."
  keywords="wall of love embed, testimonial widget, customer review showcase, video testimonials wall"
  path="/embed/wall"
/>
      <div style={pageStyle}>
        <div style={{ fontSize: 14 }}>Loading Wall of Love...</div>
      </div>
      </>
    );
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <div
          style={{
            padding: 16,
            borderRadius: 12,
            border: "1px solid #fecaca",
            background: "#fff1f2",
            color: "#b91c1c",
            fontSize: 14,
          }}
        >
          <strong>Wall of Love failed to load.</strong>
          <div style={{ marginTop: 8 }}>{error}</div>
          <div style={{ marginTop: 8, fontSize: 12 }}>
            API: {API_BASE_URL}/api/walloflove/embed/{spaceSlug}
          </div>
        </div>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div style={pageStyle}>
        <div
          style={{
            padding: 24,
            borderRadius: 16,
            border: dark ? "1px solid #334155" : "1px solid #e5e7eb",
            background: dark ? "#0f172a" : "#f9fafb",
            textAlign: "center",
          }}
        >
          <h3 style={{ margin: 0, fontSize: 18 }}>
            No testimonials published yet
          </h3>
          <p
            style={{
              margin: "8px 0 0",
              color: dark ? "#cbd5e1" : "#6b7280",
              fontSize: 14,
            }}
          >
            Approve testimonials and add them to Wall of Love from dashboard.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {space?.name && (
        <div style={{ marginBottom: 18 }}>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>
            {space.name} Wall of Love
          </h2>
          <p
            style={{
              margin: "6px 0 0",
              color: dark ? "#cbd5e1" : "#6b7280",
              fontSize: 14,
            }}
          >
            Real stories from happy customers
          </p>
        </div>
      )}

      {layout === "carousel" ? (
        <Carousel
          items={items}
          dark={dark}
          hideDate={hideDate}
          hideSource={hideSource}
          showCaptions={showCaptions}
        />
      ) : layout === "marquee" ? (
        <Marquee
          items={items}
          dark={dark}
          hideDate={hideDate}
          hideSource={hideSource}
          showCaptions={showCaptions}
        />
      ) : (
        <GridWall
          items={items}
          dark={dark}
          hideDate={hideDate}
          hideSource={hideSource}
          showCaptions={showCaptions}
          videoOnly={layout === "video-wall"}
        />
      )}
    </div>
  );
}

function GridWall({
  items,
  dark,
  hideDate,
  hideSource,
  showCaptions,
  videoOnly = false,
}) {
  const visibleItems = videoOnly
    ? items.filter((item) => item.videoUrl)
    : items;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        gap: 16,
        alignItems: "start",
      }}
    >
      {visibleItems.map((item, index) => (
        <TestimonialCard
          key={item._id || index}
          item={item}
          dark={dark}
          hideDate={hideDate}
          hideSource={hideSource}
          showCaptions={showCaptions}
        />
      ))}
    </div>
  );
}

function Carousel({ items, dark, hideDate, hideSource, showCaptions }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!items.length) return undefined;

    const id = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 4500);

    return () => window.clearInterval(id);
  }, [items.length]);

  const item = items[index];

  return (
    <div>
      <TestimonialCard
        item={item}
        dark={dark}
        hideDate={hideDate}
        hideSource={hideSource}
        showCaptions={showCaptions}
      />

      <div
        style={{
          marginTop: 14,
          display: "flex",
          justifyContent: "center",
          gap: 8,
        }}
      >
        {items.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`Go to testimonial ${i + 1}`}
            style={{
              width: i === index ? 18 : 8,
              height: 8,
              borderRadius: 999,
              border: 0,
              cursor: "pointer",
              background: i === index ? "#db2777" : dark ? "#475569" : "#d1d5db",
            }}
          />
        ))}
      </div>
    </div>
  );
}

function Marquee({ items, dark, hideDate, hideSource, showCaptions }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        overflowX: "auto",
        paddingBottom: 8,
      }}
    >
      {items.map((item, index) => (
        <div key={item._id || index} style={{ minWidth: 280, maxWidth: 340 }}>
          <TestimonialCard
            item={item}
            dark={dark}
            hideDate={hideDate}
            hideSource={hideSource}
            showCaptions={showCaptions}
          />
        </div>
      ))}
    </div>
  );
}

function TestimonialCard({
  item,
  dark,
  hideDate,
  hideSource,
  showCaptions,
}) {
  const name = item.name || "Anonymous";
  const role = item.role || item.designation || "";
  const email = item.email || "";
  const created = item.createdAt
    ? new Date(item.createdAt).toLocaleDateString()
    : "";
  const source = item.source || item.badge || "";
  const text = item.testimonialText || item.feedback || item.message || "";
  const photoUrl = resolveFileUrl(item.photoUrl || item.profileImage || "");
  const videoUrl = resolveFileUrl(item.videoUrl || "");

  return (
    <article
      style={{
        borderRadius: 18,
        padding: 16,
        background: dark ? "#0f172a" : "#ffffff",
        border: dark ? "1px solid #334155" : "1px solid #e5e7eb",
        boxShadow: dark
          ? "0 12px 30px rgba(0,0,0,0.35)"
          : "0 12px 30px rgba(15,23,42,0.08)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={name}
            style={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
        ) : (
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #db2777, #f97316)",
              color: "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
            }}
          >
            {getInitials(name)}
          </div>
        )}

        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 15 }}>{name}</div>

          <div
            style={{
              fontSize: 12,
              color: dark ? "#cbd5e1" : "#6b7280",
              marginTop: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {role || email}
            {!hideDate && created ? ` · ${created}` : ""}
            {!hideSource && source ? ` · ${source}` : ""}
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: 12,
          color: "#f59e0b",
          fontSize: 15,
          letterSpacing: 1,
        }}
      >
        {getStars(item.stars || item.rating || 5)}
      </div>

      {showCaptions && text && (
        <p
          style={{
            margin: "12px 0 0",
            color: dark ? "#e2e8f0" : "#374151",
            lineHeight: 1.6,
            fontSize: 14,
          }}
        >
          {text}
        </p>
      )}

      {videoUrl && (
        <video
          src={videoUrl}
          controls
          playsInline
          preload="metadata"
          style={{
            width: "100%",
            maxHeight: 280,
            borderRadius: 12,
            marginTop: 14,
            objectFit: "cover",
            background: "#000000",
          }}
        />
      )}
    </article>
  );
}
